import axios, { AxiosError, AxiosInstance } from "axios"
import { axiosInstance, axiosInstanceV1 } from "./services/v2/axiosInstance"
import store, { setSessionToken } from "./globals"
import { setUser } from "./redux/reducers/sessionReducer"
import { marcarCambioDeClave } from "./cambioDeClave"

/*
	Un solo lugar que decide qué pasa cuando el servidor contesta 401.

	Antes cada pantalla lo tragaba a su manera: Horarios decía "La búsqueda no pudo
	completarse", Cerrar sesión decía "Las credenciales proporcionadas no son válidas"
	y dejaba el token muerto guardado, y la persona quedaba dando vueltas entre el error
	y el login sin saber por qué (videos de Torreón, 2026-09-29).

	Ahora: cualquier 401 con token → se borra la sesión de los tres lugares donde vive
	(localStorage.accessToken, persist:root y el estado en memoria), se guarda el motivo
	que mandó el servidor (sesion_cerrada / sesion_vencida / sesion_inactiva) y se va al
	login con una navegación completa. El login lo lee y lo muestra.

	El 401 del propio login (clave equivocada) y los de restablecer clave no cuentan.
*/
const MOTIVO_KEY = "wapp_motivo_salida"
const RUTAS_LIBRES = ["/access/_login", "/access/_pwdReset", "/access/_pwdResetConfirm", "/access/_whatsappEntrar", "/access/_invitacionAceptar"]
const MENSAJE_POR_DEFECTO = "Tu sesión ya no es válida. Vuelve a iniciar sesión."

let saliendo = false

/*
	Varias pestañas, un solo token (Esteban, 2026-10-12: Carla Urbina 12 entradas y 10
	cierres en un día). Todas las pestañas del navegador comparten localStorage.accessToken,
	pero cada una guarda en memoria el token con el que arrancó. Cuando una pestaña entra
	de nuevo, las demás seguían mandando el token viejo; al cerrarlo el servidor, la
	primera que recibía el 401 borraba localStorage (¡también el token nuevo!) y mandaba
	a todas al login: cada entrada sacaba a otra pestaña y así en rueda.

	Ahora, antes de dar por muerta la sesión, la pestaña mira si en localStorage hay un
	token más nuevo de la MISMA persona; si lo hay, lo adopta y repite la petición. Y se
	entera al momento cuando otra pestaña entra (evento "storage").
*/
const CLAVE_TOKEN = "accessToken"
const CLAVE_DUENO = "wapp.tokenDe"

export const anotarDuenoDelToken = (userId: any) => {
	try {
		if (userId !== undefined && userId !== null) localStorage.setItem(CLAVE_DUENO, String(userId))
	} catch (_) { /* sin almacenamiento */ }
}

const tokenDeLaCabecera = (headers: any): string => {
	const valor = headers && (headers.Authorization || headers.authorization)
	return typeof valor === "string" ? valor.replace("Bearer", "").trim() : ""
}

/*
	Solo se cambian tokens que fueron de ESTA pestaña: una petición con otra cabecera
	Bearer (otro servicio) nunca se toca.
*/
const tokensDeEstaPestana = new Set<string>()
const anotarTokensDeLaPestana = () => {
	const estado: any = store.getState()
	const a = estado.sessionToken?.value
	const b = estado.rootReducer?.sessionState?.user?.token
	if (a) tokensDeEstaPestana.add(String(a).trim())
	if (b) tokensDeEstaPestana.add(String(b).trim())
}

/** El token guardado por la última entrada en este navegador, si es otro distinto de `usado` y de la misma persona. */
const tokenMasNuevo = (usado: string): string | null => {
	try {
		anotarTokensDeLaPestana()
		if (!tokensDeEstaPestana.has(usado)) return null
		const guardado = (localStorage.getItem(CLAVE_TOKEN) || "").trim()
		if (guardado === "" || guardado === usado) return null
		const dueno = localStorage.getItem(CLAVE_DUENO)
		const yo = (store.getState() as any).rootReducer?.sessionState?.user?.id
		if (dueno && yo !== undefined && yo !== null && String(dueno) !== String(yo)) return null
		return guardado
	} catch (_) {
		return null
	}
}

const adoptarToken = (token: string) => {
	const estado: any = store.getState()
	if (estado.sessionToken?.value !== token) store.dispatch(setSessionToken(token))
	const usuario = estado.rootReducer?.sessionState?.user
	if (usuario && usuario.token !== token) store.dispatch(setUser({ ...usuario, token }))
}

const escucharOtrasPestanas = () => {
	window.addEventListener("storage", (e: StorageEvent) => {
		if (e.key !== CLAVE_TOKEN || !e.newValue) return
		const actual = (store.getState() as any).sessionToken?.value
		if (!actual) return //esta pestaña no tiene sesión (está en el login): nada que adoptar
		const nuevo = tokenMasNuevo(String(actual).trim())
		if (nuevo) adoptarToken(nuevo)
	})
}

/** Antes de salir: si la petición llevaba un token viejo y la cabecera es reemplazable, se usa el más nuevo. */
const alPedir = (config: any) => {
	const usado = tokenDeLaCabecera(config && config.headers)
	if (usado !== "") {
		const nuevo = tokenMasNuevo(usado)
		if (nuevo) {
			config.headers.Authorization = "Bearer " + nuevo
			if (config.headers.authorization) delete config.headers.authorization
			adoptarToken(nuevo)
		}
	}
	return config
}

const esRutaLibre = (url: string): boolean => RUTAS_LIBRES.some((ruta) => url.indexOf(ruta) >= 0)

const llevabaToken = (error: AxiosError): boolean => {
	const headers: any = error.config && error.config.headers
	const valor = headers && (headers.Authorization || headers.authorization)
	return typeof valor === "string" && valor.replace("Bearer", "").trim() !== ""
}

export const borrarSesionLocal = (tokenMuerto?: string) => {
	try {
		//Si otra pestaña ya entró con un token nuevo, ese no se toca: solo se borra el que murió.
		const guardado = (localStorage.getItem("accessToken") || "").trim()
		if (tokenMuerto && guardado !== "" && guardado !== tokenMuerto) return
		localStorage.removeItem("accessToken")
		localStorage.removeItem("persist:root")
		localStorage.removeItem("currentRole")
	} catch (_) { /* sin almacenamiento: no hay nada que borrar */ }
}

export const guardarMotivoDeSalida = (mensaje: string, codigo?: string) => {
	try {
		sessionStorage.setItem(MOTIVO_KEY, JSON.stringify({ mensaje, codigo: codigo || "", cuando: Date.now() }))
	} catch (_) { /* modo privado: se pierde el texto, no la salida */ }
}

/*
	Lo lee la pantalla de login. NO se borra al leer: cuando muere la sesión, la app
	vieja alcanza a navegar al login por su cuenta (y a leer el motivo) antes de que
	llegue la navegación completa; si se borrara en esa primera lectura, el login
	nuevo ya no tendría nada que mostrar. Se borra al entrar con éxito, y de todos
	modos caduca a los dos minutos.
*/
export const leerMotivoDeSalida = (): string | null => {
	try {
		const crudo = sessionStorage.getItem(MOTIVO_KEY)
		if (!crudo) {
			return null
		}
		const guardado = JSON.parse(crudo)
		if (!guardado || typeof guardado.mensaje !== "string" || Date.now() - Number(guardado.cuando || 0) > 2 * 60 * 1000) {
			sessionStorage.removeItem(MOTIVO_KEY)
			return null
		}
		return guardado.mensaje
	} catch (_) {
		return null
	}
}

/** El error_code del 401 que sacó a la persona (p. ej. "sesion_otro_dispositivo" para el título del aviso). */
export const leerCodigoDeSalida = (): string => {
	try {
		const guardado = JSON.parse(sessionStorage.getItem(MOTIVO_KEY) || "null")
		return guardado && typeof guardado.codigo === "string" ? guardado.codigo : ""
	} catch (_) {
		return ""
	}
}

export const olvidarMotivoDeSalida = () => {
	try {
		sessionStorage.removeItem(MOTIVO_KEY)
	} catch (_) { /* nada */ }
}

export const salirPorSesionInvalida = (mensaje?: string, tokenMuerto?: string, codigo?: string) => {
	if (saliendo) {
		return
	}
	saliendo = true
	borrarSesionLocal(tokenMuerto)
	guardarMotivoDeSalida(mensaje && mensaje.trim() !== "" ? mensaje : MENSAJE_POR_DEFECTO, codigo)

	if (window.location.pathname.indexOf("/access") === 0) {
		//Ya estamos en el login (sesión guardada que murió): basta con recargar limpio.
		window.location.reload()
		return
	}
	recordarDestino()
	window.location.replace("/access")
}

const alFallar = (error: AxiosError) => {
	const respuesta: any = error && error.response
	const url = (error && error.config && error.config.url) || ""

	//Contraseña temporal: el servidor no deja hacer nada hasta cambiarla (cambioDeClave.tsx).
	if (respuesta && respuesta.status === 403 && (respuesta.data as any)?.error_code === "cambiar_clave") {
		marcarCambioDeClave()
	}
	if (respuesta && respuesta.status === 401 && !esRutaLibre(url) && llevabaToken(error)) {
		const config: any = error.config
		const usado = tokenDeLaCabecera(config && config.headers)
		const nuevo = tokenMasNuevo(usado)
		if (nuevo && config && !config._conTokenNuevo) {
			//Otra pestaña ya entró: esta adopta su token y repite la petición, sin salir.
			adoptarToken(nuevo)
			const repetir = { ...config, _conTokenNuevo: true, headers: { ...(config.headers || {}), Authorization: "Bearer " + nuevo } }
			return axios.request(repetir)
		}
		const datos: any = respuesta.data
		salirPorSesionInvalida(datos && typeof datos.message === "string" ? datos.message : undefined, usado, datos && datos.error_code)
	}
	return Promise.reject(error)
}

export const instalarGuardiaDeSesion = () => {
	const instancias: AxiosInstance[] = [axios, axiosInstance, axiosInstanceV1]
	instancias.forEach((instancia) => {
		instancia.interceptors.request.use(alPedir)
		instancia.interceptors.response.use((r) => r, alFallar)
	})
	store.subscribe(anotarTokensDeLaPestana)
	escucharOtrasPestanas()
}

/**
 * Enlaces directos (Esteban, 2026-10-09): si alguien abre un enlace de WAPP (un documento de WappDocs, una carpeta…) sin
 * sesión, se guarda a dónde iba y después de entrar se le lleva ahí, en vez de dejarlo en el Panel.
 */
export const recordarDestino = (): void => {
	const p = window.location.pathname
	if (p === "/" || p.indexOf("/access") === 0) return
	try { sessionStorage.setItem("wapp.volverA", p + window.location.search) } catch (e) { /* sin almacenamiento */ }
}
export const tomarDestino = (): string => {
	try {
		const d = sessionStorage.getItem("wapp.volverA")
		sessionStorage.removeItem("wapp.volverA")
		return d && d.charAt(0) === "/" && d.charAt(1) !== "/" ? d : "/"
	} catch (e) { return "/" }
}
